import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarList, Btn, Chip, KPI, KPIRow, Loading, Msg, Section, Select, SubTabs, useAdminGet,
} from '../../kit';
import { PeriodPicker, type Period, periodText, usePeriodParams } from '../../period';
import { fmtN } from '../../monthly';
import { Check } from '../emailKit';
import { AdminLink } from '../../adminNav';
import { GroupNote, GroupSelect, useEmailGroups } from '../invites/shared';
import WeeklyTable, { sumWeek, type WeeklyRow } from './WeeklyTable';
import TemplateStats from './TemplateStats';
import CampaignStats from './CampaignStats';
import SendsTable from './SendsTable';
import {
  AutomatedToggle, Change, EngagementNote, KNOWN_CATEGORIES, MODES, MODE_LABELS, ModePill, N, Pct, SECTION,
  SOURCE_WORD, WINDOWS, WINDOW_DAYS, WINDOW_LABELS, type WeekMetric, type WeekPoint, WeekTrend, arr, categoryWord,
  dayText, downloadStatsExport, learnCategoryLabels, num, pctText, rangeOf, rateOf, shareHint, str, weekEnd,
} from './shared';

/**
 * Email Center → Dashboard (owner, 2026-10-11): how every member e-mail did —
 * campaigns, MEMAC, the automatic mails and researcher outreach — over a
 * period, overall, week by week (the MEMAC analytics table), per template,
 * per campaign and per person.
 *
 * One filter bar drives every view: the period (days and weeks in India time,
 * weeks starting Monday), manual vs automatic, the MEMAC category, one
 * template, one bulk group (2026-10-15: a bulk add, an Account-origin batch
 * or a People import — only the e-mails to its members), and whether the
 * team's own addresses count (left out by default).
 * The KPI row compares the period with the one just before it, of the same
 * length. Backend: Backend/admin_email_stats.py (section "email", stats_*).
 *
 * Opens and clicks are people's by default (owner, 2026-10-25): instant reads,
 * Apple MPP and scanner opens, and bot clicks have their own KPIs ("Instant /
 * auto opens", "Bot clicks") and tags in Individual sends; "Include automated"
 * (automated=1) shows the raw SendGrid numbers everywhere on the page.
 *
 * EmailPanel keeps this mounted once opened, so moving to another Email
 * Center tab and back keeps the filters.
 */

const VIEWS = ['Overview', 'Weekly table', 'By template', 'Campaigns', 'Individual sends'];

/** A deep link's dashboard state (EmailPanel: tab=dashboard&template=…&mode=…&view=…&q=…&bulk=…;
 *  `group` is the bulk group id, sent in the URL as bulk= because group= names the admin group). */
export type DashboardPreset = {
  template?: string; mode?: string; category?: string; view?: string; q?: string; status?: string;
  group?: string; nonce: string;
};

type Known = { name: string; category: string };

const EmailDashboard: React.FC<{ preset?: DashboardPreset | null }> = ({ preset }) => {
  const [period, setPeriod] = useState<Period>({ preset: '12w', from: '', to: '' });
  const { params: pp } = usePeriodParams(period);
  const ppKey = JSON.stringify(pp);
  // rangeOf reads today's date: worked out once per period choice, so the
  // params (and the fetch) stay put across renders.
  const range = useMemo(() => rangeOf(pp), [ppKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [mode, setMode] = useState('all');
  const [category, setCategory] = useState('');
  const [template, setTemplate] = useState('');
  const [group, setGroup] = useState('');
  const groups = useEmailGroups();
  const [withTeam, setWithTeam] = useState(false);
  const [withSignIn, setWithSignIn] = useState(false);
  const [automated, setAutomated] = useState(false);
  const [view, setView] = useState(VIEWS[0]);
  const [stamp, setStamp] = useState(0);
  const [sendsPreset, setSendsPreset] = useState<{ status?: string; q?: string; nonce: number } | null>(null);
  const [dlBusy, setDlBusy] = useState(false);
  const [dlErr, setDlErr] = useState('');
  const [dlInfo, setDlInfo] = useState('');
  // Refresh asks the server to rebuild (refresh=1 drops its 90-second cache);
  // a filter change after that reads the cache again.
  const [fresh, setFresh] = useState(false);

  useEffect(() => {
    if (!preset) return;
    if (preset.template !== undefined) setTemplate(preset.template);
    if (preset.mode && MODES.includes(preset.mode)) setMode(preset.mode);
    if (preset.category !== undefined) setCategory(preset.category);
    if (preset.group !== undefined) setGroup(preset.group);
    const v = VIEWS.find(x => x.toLowerCase().replace(/[^a-z]+/g, '_') === (preset.view || '').toLowerCase()
      || x.toLowerCase() === (preset.view || '').toLowerCase());
    if (preset.q || preset.status) {
      setSendsPreset({ q: preset.q || '', status: preset.status || '', nonce: Date.now() });
      setView('Individual sends');
    } else if (v) setView(v);
  }, [preset?.nonce]);   // eslint-disable-line react-hooks/exhaustive-deps

  const filters = useMemo(() => {
    const f: Record<string, string> = { mode, exclude_team: withTeam ? '0' : '1' };
    if (withSignIn) f.include_sign_in = '1';
    if (range.from) f.from = range.from;
    if (range.to) f.to = range.to;
    if (category) f.category = category;
    if (template) f.template = template;
    if (group) f.group = group;
    if (automated) f.automated = '1';
    return f;
  }, [range, mode, category, template, group, withTeam, withSignIn, automated]);
  const fKey = JSON.stringify(filters);
  const reads = fresh ? { ...filters, refresh: '1' } : filters;
  const ov = useAdminGet(SECTION, 'stats_overview', reads, [fKey, stamp]);
  const wk = useAdminGet(SECTION, 'stats_weekly', reads, [fKey, stamp]);
  useEffect(() => { if (fresh && !ov.loading) setFresh(false); }, [fresh, ov.loading]);
  const o: any = ov.data || {};
  const w: any = wk.data || {};

  // Template names and categories as the server reports them, remembered
  // across filter changes so the template picker keeps every option after
  // the page is narrowed to one.
  const known = useRef(new Map<string, Known>());
  const [knownTick, setKnownTick] = useState(0);
  useEffect(() => {
    let added = false;
    learnCategoryLabels(o.by_category);
    [...arr(o.by_template), ...arr(w.rows)].forEach((r: any) => {
      const k = str(r?.template_key);
      if (!k) return;
      const name = str(r.template_name) || str(r.name) || k;
      const prev = known.current.get(k);
      if (!prev || (prev.name === k && name !== k) || (!prev.category && r.category)) {
        known.current.set(k, { name, category: str(r.category) || prev?.category || '' });
        added = true;
      }
    });
    if (added) setKnownTick(t => t + 1);
  }, [ov.data, wk.data]);   // eslint-disable-line react-hooks/exhaustive-deps
  const tplName = useCallback((k: string) => known.current.get(k)?.name || k,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [knownTick]);
  const tplOptions = Array.from(known.current.entries())
    .sort((a, b) => a[1].name.localeCompare(b[1].name));
  if (template && !known.current.has(template)) tplOptions.unshift([template, { name: template, category: '' }]);
  const catOptions = Array.from(new Set([
    ...KNOWN_CATEGORIES,
    ...arr(o.by_category).map((c: any) => str(c?.category ?? c?.key)).filter(Boolean),
    ...Array.from(known.current.values()).map(v => v.category).filter(Boolean),
    ...(category ? [category] : []),
  ]));

  const k: any = o.kpis || {};
  const prev: any = o.previous?.kpis || o.previous || null;
  const period_ = o.period || {};
  const label = periodText({ preset: pp.window || '12w', from: pp.from || '', to: pp.to || '' }, WINDOW_LABELS);
  const prevPeriod = o.previous_period || o.previous?.period || null;
  const vs = prevPeriod?.from && prevPeriod?.to
    ? `${dayText(prevPeriod.from)} – ${dayText(prevPeriod.to)}` : 'the period before, of the same length';

  const chips: { label: string; value: string; clear: () => void }[] = [];
  if (mode !== 'all') chips.push({ label: 'Sent', value: MODE_LABELS[mode] || mode, clear: () => setMode('all') });
  if (category) chips.push({ label: 'Category', value: categoryWord(category), clear: () => setCategory('') });
  if (template) chips.push({ label: 'E-mail', value: tplName(template), clear: () => setTemplate('') });
  const g = groups.byId(group);
  const groupEcho: any = o.filters?.group && typeof o.filters.group === 'object' ? o.filters.group : null;
  if (group) chips.push({ label: 'Group', value: g?.label || str(groupEcho?.label) || group, clear: () => setGroup('') });
  if (withTeam) chips.push({ label: 'Team', value: 'included', clear: () => setWithTeam(false) });
  if (withSignIn) chips.push({ label: 'Sign-in codes', value: 'included', clear: () => setWithSignIn(false) });
  if (automated) chips.push({ label: 'Opens and clicks', value: 'automated included', clear: () => setAutomated(false) });

  const pickTemplate = (key: string) => setTemplate(t => (t === key ? '' : key));
  const toSends = (opts: { status?: string; template?: string }) => {
    if (opts.template !== undefined) setTemplate(opts.template);
    setSendsPreset({ status: opts.status || '', nonce: Date.now() });
    setView('Individual sends');
  };
  const exportNow = async () => {
    setDlBusy(true); setDlErr(''); setDlInfo('');
    const r = await downloadStatsExport(filters);
    setDlBusy(false);
    setDlErr(r.error); setDlInfo(r.info);
  };
  const shownFrom = str(period_.from) || range.from || '';
  const shownTo = str(period_.to) || range.to || '';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="text-sm text-grayscale-1200 max-w-2xl">
          How our e-mails to members did — campaigns, MEMAC, the automatic mails and researcher outreach.
          Days and weeks are India time; a week starts on Monday.
          {shownFrom && <span className="text-grayscale-1100"> Showing {dayText(shownFrom)} – {dayText(shownTo) || 'today'}.</span>}
        </div>
        <PeriodPicker value={period} onChange={setPeriod} options={WINDOWS} labels={WINDOW_LABELS}
          presetDays={WINDOW_DAYS} hint="Both days included (India time). Leave one blank for open-ended." />
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={mode} onChange={setMode} options={MODES} labels={MODE_LABELS} />
        <Select value={category} onChange={setCategory} options={['', ...catOptions]}
          labels={{ '': 'Every category', ...Object.fromEntries(catOptions.map(c => [c, categoryWord(c)])) }} />
        <Select value={template} onChange={setTemplate} options={['', ...tplOptions.map(([key]) => key)]}
          labels={{ '': 'Every e-mail', ...Object.fromEntries(tplOptions.map(([key, v]) => [key, v.name])) }} />
        {(groups.groups.length > 0 || group) && (
          <GroupSelect value={group} onChange={setGroup} groups={groups.groups} all="Everyone" />
        )}
        <Check checked={withTeam} onChange={setWithTeam}>Count the team's own addresses</Check>
        <Check checked={withSignIn} onChange={setWithSignIn}>Include sign-in codes</Check>
        <AutomatedToggle value={automated} onChange={setAutomated} />
        <span className="flex-1" />
        <Btn small onClick={() => { setFresh(true); setStamp(s => s + 1); }}
          title="Count again from the database (otherwise results are kept for 90 seconds)">Refresh</Btn>
        <Btn small kind="primary" disabled={dlBusy} onClick={exportNow}
          title="The weekly table and every send (same filters) as an Excel workbook">
          {dlBusy ? 'Preparing…' : 'Download Excel'}
        </Btn>
      </div>
      {chips.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap -mt-2">
          {chips.map(c => <Chip key={c.label} label={c.label} value={c.value} onClear={c.clear} />)}
          <button type="button" className="text-xs text-grayscale-1100 underline underline-offset-2"
            onClick={() => { setMode('all'); setCategory(''); setTemplate(''); setGroup(''); setWithTeam(false); setWithSignIn(false); setAutomated(false); }}>clear all</button>
        </div>
      )}
      {group && (
        <GroupNote group={g} id={group} fallbackLabel={str(groupEcho?.label)} people={groupEcho?.people}>
          {' '}<AdminLink to={{ group: 'email', tab: 'invites', bulk: group }}
            title="Their invitation e-mails run by run, and who signed in after">Their invites →</AdminLink>
        </GroupNote>
      )}
      {dlErr && <Msg kind="error">Excel download: {dlErr}</Msg>}
      {dlInfo && <Msg kind="ok">Downloaded {dlInfo}</Msg>}
      {ov.error && <Msg kind="error">{ov.error}</Msg>}

      {ov.loading && !ov.data ? <Loading label="Counting e-mails…" /> : ov.data && (
        <KPIRow>
          <KPI label="Sent" value={<Val v={k.sent} cur={num(k.sent)} prev={prev?.sent} kind="count" vs={vs} />}
            hint={label} onClick={() => toSends({})} />
          <KPI label="Delivered" value={<Val v={k.delivered} cur={num(k.delivered)} prev={prev?.delivered} kind="count" vs={vs} />}
            hint={k.sent ? `${pctText(num(k.delivered) / num(k.sent))} of sent` : undefined}
            onClick={() => toSends({ status: 'delivered' })} />
          <KPI label={automated ? 'Open rate (all)' : 'Open rate'} value={<RateVal row={k} rk="open_rate" prev={prev} vs={vs} />}
            hint={'delivered' in k && !num(k.delivered) ? 'nothing delivered'
              : rateOf(k, 'open_rate') === null ? 'only plain-text e-mails here'
              : `${fmtN(num(k.opened))} opened${automated ? ''
                : k.raw_open_rate !== undefined && k.raw_open_rate !== null ? ` by people · ${pctText(Number(k.raw_open_rate))} with automated` : ''}`}
            onClick={() => toSends({ status: 'opened' })} />
          <KPI label={automated ? 'Click rate (all)' : 'Click rate'} value={<RateVal row={k} rk="click_rate" prev={prev} vs={vs} />}
            hint={`${fmtN(num(k.clicked))} clicked${automated ? ''
              : k.raw_click_rate !== undefined && k.raw_click_rate !== null ? ` by people · ${pctText(Number(k.raw_click_rate))} with bots` : ''}`}
            onClick={() => toSends({ status: 'clicked' })} />
          <KPI label="CTOR" value={<RateVal row={k} rk="ctor" prev={prev} vs={vs} />} hint="clicks ÷ opens" />
          {'auto_opened' in k && (
            <KPI label="Instant / auto opens"
              value={<N v={k.auto_opened} />}
              hint={shareHint(k.auto_open_share, 'opened e-mails had only automated opens') || 'opened only by machines'}
              onClick={() => toSends({ status: 'auto_opened' })} />
          )}
          {'bot_clicked' in k && (
            <KPI label="Bot clicks"
              value={<N v={k.bot_clicked} />}
              hint={shareHint(k.bot_click_share, 'clicked e-mails had only bot clicks') || 'clicked only by scanners'}
              onClick={() => toSends({ status: 'bot_clicked' })} />
          )}
          <KPI label="Bounced" value={<Val v={k.bounced} cur={num(k.bounced)} prev={prev?.bounced} kind="count" vs={vs} lower />}
            hint={rateOf(k, 'bounce_rate') !== undefined ? `${pctText(rateOf(k, 'bounce_rate'))} of sent` : undefined}
            onClick={() => toSends({ status: 'bounced' })} />
          <KPI label="Primary CTA clicks"
            value={k.cta_clicks === null ? <span className="text-grayscale-1100" title="None of these e-mails has a countable button">n/a</span>
              : <Val v={k.cta_clicks} cur={num(k.cta_clicks)} prev={prev?.cta_clicks} kind="count" vs={vs} />}
            hint={rateOf(k, 'cta_click_rate') !== undefined && k.cta_click_rate !== null
              ? `${pctText(rateOf(k, 'cta_click_rate'))} of delivered with a button` : 'people who clicked the main button'} />
          <KPI label="Unsubscribed" value={<Val v={k.unsubscribed} cur={num(k.unsubscribed)} prev={prev?.unsubscribed} kind="count" vs={vs} lower />}
            hint={k.member_unsubscribes !== undefined && k.member_unsubscribes !== null
              ? `${fmtN(num(k.member_unsubscribes))} through our own unsubscribe link` : 'SendGrid unsubscribe events'} />
        </KPIRow>
      )}
      {ov.data && (
        <div className="text-[11px] text-grayscale-1100 -mt-2">
          ▲▼ = change against {vs}. Click a number to see those sends one by one.
        </div>
      )}
      {ov.data && <EngagementNote eng={o.engagement || o.coverage?.engagement} automated={automated} />}

      <SubTabs tabs={VIEWS} active={view} onChange={setView} />

      {view === 'Overview' && (
        <Overview o={o} w={w} wkState={wk} range={range} template={template} category={category}
          onTemplate={pickTemplate} onCategory={c => setCategory(x => (x === c ? '' : c))}
          onMode={m => setMode(x => (x === m ? 'all' : m))} onWeekly={() => setView('Weekly table')}
          onAllTemplates={() => setView('By template')} />
      )}
      {view === 'Weekly table' && (
        <Section title={`Week by week, per e-mail (${label})`}>
          {wk.error && <Msg kind="error">{wk.error}</Msg>}
          {wk.loading && !wk.data ? <Loading /> : (
            <WeeklyTable data={w} from={range.from} to={range.to} tplName={tplName} onTemplate={pickTemplate} />
          )}
        </Section>
      )}
      {view === 'By template' && (
        <Section title={`Per e-mail (${label})`}>
          {ov.loading && !ov.data ? <Loading /> : (
            <TemplateStats rows={arr(o.by_template)} tplName={tplName} active={template}
              onTemplate={pickTemplate} onSends={key => toSends({ template: key })} />
          )}
        </Section>
      )}
      {view === 'Campaigns' && (
        <Section title={`Per campaign (${label})`}>
          <CampaignStats range={range} template={template} mode={mode} group={group} tplName={tplName} stamp={stamp}
            automated={automated} />
        </Section>
      )}
      {view === 'Individual sends' && (
        <Section title={`Every send (${label})`}>
          <SendsTable filters={filters} tplName={tplName} stamp={stamp} preset={sendsPreset} />
        </Section>
      )}

      <Coverage c={o.coverage} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// KPI values with their change
// ---------------------------------------------------------------------------
const Val: React.FC<{
  v: any; cur: number; prev: any; kind: 'count' | 'rate'; vs: string; lower?: boolean;
}> = ({ v, cur, prev, kind, vs, lower }) => (
  <span className="flex items-baseline gap-2 flex-wrap">
    <N v={v} />
    {prev !== undefined && prev !== null && <Change cur={cur} prev={num(prev)} kind={kind} vs={vs} lowerIsBetter={lower} />}
  </span>
);
const RateVal: React.FC<{ row: any; rk: string; prev: any; vs: string }> = ({ row, rk, prev, vs }) => {
  // Nothing delivered: no rate at all (not "n/a", which means plain text).
  if ('delivered' in (row || {}) && !num(row.delivered)) return <span>—</span>;
  const cur = rateOf(row, rk);
  const before = prev ? rateOf(prev, rk) : undefined;
  return (
    <span className="flex items-baseline gap-2 flex-wrap">
      <Pct v={cur} />
      {typeof cur === 'number' && typeof before === 'number' && <Change cur={cur} prev={before} kind="rate" vs={vs} />}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Overview: the weekly trend, manual vs automatic, categories, top e-mails
// ---------------------------------------------------------------------------
const TREND: WeekMetric[] = [
  { key: 'sent', label: 'Sent', kind: 'count', series: [
    { key: 'sent_manual', label: 'Manual', tone: 'bg-primary-500' },
    { key: 'sent_auto', label: 'Automatic', tone: 'bg-secondary-400' },
    { key: 'sent_other', label: 'Other (admin action, test)', tone: 'bg-primary-300' },
  ] },
  { key: 'open_rate', label: 'Open rate', kind: 'rate', note: 'HTML e-mails only — plain text has no opens.' },
  { key: 'click_rate', label: 'Click rate', kind: 'rate' },
  { key: 'ctor', label: 'CTOR', kind: 'rate', note: 'Clicks ÷ opens.' },
];

const Overview: React.FC<{
  o: any; w: any; wkState: { loading: boolean; error: string; data: any };
  range: { from?: string; to?: string }; template: string; category: string;
  onTemplate: (k: string) => void; onCategory: (c: string) => void; onMode: (m: string) => void;
  onWeekly: () => void; onAllTemplates: () => void;
}> = ({ o, w, wkState, range, template, category, onTemplate, onCategory, onMode, onWeekly, onAllTemplates }) => {
  const rows: WeeklyRow[] = useMemo(() => arr(w.rows).filter((r: any) => r && r.week), [w]);
  const points: WeekPoint[] = useMemo(() => {
    const weeks = new Set<string>();
    arr(w.weeks).forEach((x: any) => { const k = str(x?.week ?? x).slice(0, 10); if (k) weeks.add(k); });
    rows.forEach(r => weeks.add(r.week.slice(0, 10)));
    const totals = arr(w.totals_by_week);
    const said = new Map<string, boolean>();
    arr(w.weeks).forEach((x: any) => { if (typeof x?.partial === 'boolean') said.set(str(x.week).slice(0, 10), x.partial); });
    return Array.from(weeks).sort().map(week => {
      const mine = rows.filter(r => r.week.slice(0, 10) === week);
      const s = sumWeek(mine);
      const t = totals.find((x: any) => str(x?.week).slice(0, 10) === week) || {};
      const by = (m: string) => mine.filter(r => str(r.mode) === m).reduce((a, r) => a + num(r.sent), 0);
      const manual = by('manual'); const auto = by('auto');
      const merged: any = { ...s, ...t };
      return {
        week,
        partial: said.has(week) ? !!said.get(week)
          : (range.from ? week < range.from : false) || (range.to ? weekEnd(week) > range.to : false),
        sent: num(merged.sent),
        sent_manual: manual, sent_auto: auto, sent_other: Math.max(0, num(s.sent) - manual - auto),
        open_rate: rateOf(merged, 'open_rate') ?? null,
        click_rate: rateOf(merged, 'click_rate') ?? null,
        ctor: rateOf(merged, 'ctor') ?? null,
      };
    });
  }, [w, rows, range.from, range.to]);
  const metrics = points.some(p => p.sent_other > 0) ? TREND
    : TREND.map(m => (m.series ? { ...m, series: m.series.filter(s => s.key !== 'sent_other') } : m));

  const MODE_ORDER = ['manual', 'auto'];
  const rank = (m: any) => { const i = MODE_ORDER.indexOf(str(m)); return i < 0 ? 9 : i; };
  const byMode: any[] = arr(o.by_mode).slice().sort((a: any, b: any) => rank(a?.mode) - rank(b?.mode));
  const byCat: any[] = arr(o.by_category);
  const byTpl: any[] = arr(o.by_template).slice().sort((a: any, b: any) => num(b.sent) - num(a.sent));
  const catItems = byCat.map((c: any) => ({ key: str(c.category ?? c.key), sent: num(c.sent) }))
    .filter(c => c.sent > 0);
  const catLabel = (key: string) => categoryWord(key);
  const bySource = arr(o.by_source).filter((s: any) => num(s?.sent) > 0);
  const byTrigger = arr(o.by_trigger).filter((t: any) => num(t?.sent) > 0);

  return (
    <div className="flex flex-col gap-4">
      <Section title="Week by week" right={<Btn small onClick={onWeekly}>Per e-mail, week by week →</Btn>}>
        {wkState.error && <Msg kind="error">{wkState.error}</Msg>}
        {wkState.loading && !wkState.data ? <Loading /> : <WeekTrend rows={points} metrics={metrics} />}
      </Section>

      <div className="grid md:grid-cols-2 gap-4">
        <Section title="Manual vs automatic">
          {!byMode.length ? <div className="text-sm text-grayscale-1100">Nothing sent in this period.</div> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-grayscale-1100 border-b border-grayscale-300">
                  <th className="py-1.5 pr-2">Sent</th><th className="py-1.5 px-2 text-right">E-mails</th>
                  <th className="py-1.5 px-2 text-right">Open rate</th><th className="py-1.5 pl-2 text-right">Click rate</th>
                </tr>
              </thead>
              <tbody>
                {byMode.map((m: any) => (
                  <tr key={str(m.mode)} onClick={() => (m.mode === 'manual' || m.mode === 'auto') && onMode(m.mode)}
                    title={m.mode === 'manual' || m.mode === 'auto' ? 'Click to show only these' : undefined}
                    className="border-t border-grayscale-200 hover:bg-grayscale-100 cursor-pointer">
                    <td className="py-1.5 pr-2"><ModePill mode={m.mode} /></td>
                    <td className="py-1.5 px-2 text-right tabular-nums"><N v={m.sent} /></td>
                    <td className="py-1.5 px-2 text-right tabular-nums"><Pct v={rateOf(m, 'open_rate')} /></td>
                    <td className="py-1.5 pl-2 text-right tabular-nums"><Pct v={rateOf(m, 'click_rate')} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>
        <Section title="By category">
          {!catItems.length ? <div className="text-sm text-grayscale-1100">Nothing sent in this period.</div> : (
            <>
              <BarList items={catItems.map(c => ({ label: catLabel(c.key), value: c.sent }))}
                activeLabel={category ? catLabel(category) : undefined}
                onSelect={l => { const hit = catItems.find(c => catLabel(c.key) === l); if (hit) onCategory(hit.key); }} />
              <div className="text-[11px] text-grayscale-1100">
                MEMAC's categories; mails from before MEMAC are filed by what they are (billing, service…). Click one to filter.
              </div>
            </>
          )}
        </Section>
      </div>

      {(bySource.length > 0 || byTrigger.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4">
          {bySource.length > 0 && (
            <Section title="Where the numbers come from">
              <BarList items={bySource.map((s: any) => ({ label: SOURCE_WORD[str(s.source)] || str(s.source), value: num(s.sent) }))} />
              <div className="text-[11px] text-grayscale-1100">
                {bySource.some((s: any) => s.source === 'event')
                  ? <>"{SOURCE_WORD.event}" is approximate: such a mail is counted only if SendGrid reported something
                    about it. Every other source is a send ledger, so exact.</>
                  : 'Every send here comes from a send ledger, so the counts are exact.'}
              </div>
            </Section>
          )}
          {byTrigger.length > 0 && (
            <Section title="MEMAC triggers">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-grayscale-1100 border-b border-grayscale-300">
                    <th className="py-1.5 pr-2">Trigger</th><th className="py-1.5 px-2 text-right">Sent</th>
                    <th className="py-1.5 px-2 text-right">Open rate</th><th className="py-1.5 pl-2 text-right">Click rate</th>
                  </tr>
                </thead>
                <tbody>
                  {byTrigger.map((t: any) => (
                    <tr key={str(t.trigger_id)} className="border-t border-grayscale-200">
                      <td className="py-1.5 pr-2">
                        <AdminLink to={{ group: 'email', tab: 'memac', trigger: str(t.trigger_id) }}
                          title="Open this trigger in MEMAC">{str(t.trigger_id)}</AdminLink>
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums"><N v={t.sent} /></td>
                      <td className="py-1.5 px-2 text-right tabular-nums"><Pct v={rateOf(t, 'open_rate')} /></td>
                      <td className="py-1.5 pl-2 text-right tabular-nums"><Pct v={rateOf(t, 'click_rate')} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          )}
        </div>
      )}

      <Section title="Most sent e-mails" right={<Btn small onClick={onAllTemplates}>Every e-mail →</Btn>}>
        {!byTpl.length ? <div className="text-sm text-grayscale-1100">Nothing sent in this period.</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-grayscale-1100 border-b border-grayscale-300">
                  <th className="py-1.5 pr-2 w-6">#</th><th className="py-1.5 pr-2">E-mail</th>
                  <th className="py-1.5 px-2">Mode</th><th className="py-1.5 px-2 w-1/4">Sent</th>
                  <th className="py-1.5 px-2 text-right">Open rate</th><th className="py-1.5 pl-2 text-right">Click rate</th>
                </tr>
              </thead>
              <tbody>
                {byTpl.slice(0, 10).map((r: any, i: number) => {
                  const max = Math.max(1, num(byTpl[0]?.sent));
                  const key = str(r.template_key);
                  return (
                    <tr key={`${key}|${str(r.mode)}`} onClick={() => onTemplate(key)}
                      title={template === key ? 'Filtering by this — click to clear' : `${key} — click to filter by this e-mail`}
                      className={`border-t border-grayscale-200 cursor-pointer hover:bg-grayscale-100 ${template === key ? 'bg-primary-100' : ''}`}>
                      <td className="py-1.5 pr-2 text-grayscale-1100">{i + 1}</td>
                      <td className="py-1.5 pr-2 max-w-[18rem] truncate font-medium">{str(r.template_name) || str(r.name) || key}</td>
                      <td className="py-1.5 px-2"><ModePill mode={r.mode} /></td>
                      <td className="py-1.5 px-2">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-grayscale-200 rounded-full h-2.5 overflow-hidden">
                            <div className="h-2.5 bg-primary-500 rounded-full" style={{ width: `${(num(r.sent) / max) * 100}%` }} />
                          </div>
                          <span className="tabular-nums w-14 text-right"><N v={r.sent} /></span>
                        </div>
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums"><Pct v={rateOf(r, 'open_rate')} /></td>
                      <td className="py-1.5 pl-2 text-right tabular-nums"><Pct v={rateOf(r, 'click_rate')} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </div>
  );
};

// ---------------------------------------------------------------------------
// What is exact, what is approximate, and why some history is missing
// ---------------------------------------------------------------------------
const Coverage: React.FC<{ c: any }> = ({ c }) => {
  // Sources come as {key, label, sent} (or plain words from an older server).
  const srcText = (s: any) => (s && typeof s === 'object'
    ? `${str(s.label || s.key)}${s.sent !== undefined && s.sent !== null ? ` (${fmtN(num(s.sent))} in this period)` : ''}`
    : str(s));
  const exact = arr(c?.exact_sources).map(srcText).filter(Boolean);
  const approx = arr(c?.approximate_sources).map(srcText).filter(Boolean);
  const notes = arr(c?.notes).map(str).filter(Boolean);
  const since = str(c?.recorded_since);
  const leftOut = c?.left_out && typeof c.left_out === 'object' ? Object.entries(c.left_out as Record<string, any>)
    .filter(([, n]) => num(n) > 0) : [];
  const LEFT_OUT: Record<string, string> = {
    test: 'test sends', team_alert: 'alerts to the team', university: 'mail to universities',
    outreach_in_ledger: 'outreach copies already in the ledger',
  };
  return (
    <details className="bg-white rounded-xl border border-grayscale-300 p-4 text-sm text-grayscale-1200">
      <summary className="cursor-pointer font-bold text-grayscale-1300 font-grotesk text-sm">
        How these numbers are counted — what is exact and what is not
      </summary>
      <div className="flex flex-col gap-2 mt-3 text-xs leading-relaxed">
        <div>
          <b>Exact</b> — {exact.length ? exact.join(' · ') : 'campaigns, MEMAC sends, the trial mails and researcher outreach'}:
          each send has its own row in a send ledger, matched to SendGrid's events by message and address.
        </div>
        <div>
          <b>Approximate</b> — {approx.length ? approx.join(' · ') : 'other automatic mail from before this release (billing, account mails)'}:
          known only from SendGrid's events, so a mail counts as sent only when SendGrid reported something about it.
        </div>
        <div>
          <b>Missing history</b> — until this release the welcome e-mail (and an admin invitation) carried no tag and
          left no ledger row, so earlier ones cannot be counted. From this release on every member e-mail is recorded
          with its template, mode and button, so the numbers fill in going forward
          {since ? <> (recording since {dayText(since.slice(0, 10))})</> : ''}.
        </div>
        {!notes.length && (
          <>
            <div>
              <b>Rates</b> — open rate = opened (or clicked) ÷ delivered; click rate = clicked ÷ delivered; CTOR = clicked
              ÷ opened. A plain-text e-mail shows "n/a" for opens: SendGrid can only see an HTML mail being opened. Opens
              and clicks are people's: instant reads, Apple Mail privacy prefetches, scanner opens and bot clicks are
              counted apart ("Include automated" adds them back). A "mailto:" button (e.g. Talk to Concierge in older
              mails) cannot be counted as a CTA click.
            </div>
            <div>
              <b>Who is counted</b> — the address the mail was sent to (the info@ copy is dropped); the team's own
              addresses are left out unless "Count the team's own addresses" is ticked; a SendGrid event reported twice
              counts once. Weeks start on Monday, India time. Primary CTA is the e-mail's first button as the send
              recorded it, or as the template has it now for older sends.
            </div>
          </>
        )}
        {leftOut.length > 0 && (
          <div>
            <b>Never counted</b> — {leftOut.map(([k, n]) => `${LEFT_OUT[k] || k.replace(/_/g, ' ')} ${fmtN(num(n))}`).join(' · ')}.
          </div>
        )}
        {notes.length > 0 && (
          <>
            <div className="font-bold text-grayscale-1300 pt-1">From the server, for this selection</div>
            <ul className="list-disc pl-5">
              {notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </>
        )}
      </div>
    </details>
  );
};

export default EmailDashboard;
